import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "../i18n/I18nContext";
import { listProducts } from "../services/product";
import { createConsumption } from "../services/consumption";
import WizardSteps from "./shared/WizardSteps";

const TOTAL_STEPS = 2;
const todayIso = () => new Date().toISOString().split("T")[0];

const initialState = {
  productId: "",
  quantity: "",
  unitTypeId: "package",
  consumptionDate: todayIso(),
};

// Split out of the former ConsumoLayer. Same "hide cadastro from the menu"
// philosophy applied to ListaProdutosLayer/CadastrarProdutoLayer (mirrors
// sge_infantil_web/config/config.ts, where `create.history`
// (/expenses/history/create) carries `hideInMenu: true`) — this screen is
// only reachable via the "Adicionar Consumo" button on ListaConsumoLayer,
// not from the sidebar.
const CadastrarConsumoLayer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [form, setForm] = useState(initialState);
  const [products, setProducts] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const stepLabels = [t("consumo.wizard.step.data"), t("consumo.wizard.step.confirm")];

  useEffect(() => {
    listProducts().then((resp) => setProducts(resp.data || [])).catch(() => setProducts([]));
  }, []);

  const productName = (id) => products.find((p) => p.id === id)?.name || "-";
  const selectedProduct = products.find((p) => p.id === form.productId);

  const setField = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const nextStep = () => {
    if (!form.productId || !form.quantity) {
      toast.error(t("consumo.error.requiredFields"));
      return;
    }
    if (currentStep < TOTAL_STEPS) setCurrentStep(currentStep + 1);
  };
  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };
  const restart = () => {
    setForm(initialState);
    setCurrentStep(1);
    setDone(false);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const product = products.find((p) => p.id === form.productId);
      await createConsumption({
        productId: form.productId,
        product: product ? { id: product.id, name: product.name } : undefined,
        quantity: Number(form.quantity),
        unitType: form.unitTypeId,
        unitTypeId: form.unitTypeId,
        consumptionDate: form.consumptionDate,
      });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.error || t("consumo.error.create"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className='row gy-4'>
      <ToastContainer />

      <div className='col-12'>
        <div className='card'>
          <div className='card-body'>
            <h6 className='mb-4 text-xl'>{t("consumo.wizard.title")}</h6>
            <p className='text-neutral-500'>{t("consumo.wizard.subtitle")}</p>

            <div className='form-wizard'>
              <WizardSteps steps={stepLabels} currentStep={done ? TOTAL_STEPS + 1 : currentStep} />

              {!done && currentStep === 1 && (
                <fieldset className='wizard-fieldset show'>
                  <div className='row gy-3'>
                    <div className='col-sm-6'>
                      <label className='form-label'>{t("consumo.field.product")}</label>
                      <select className='form-select' value={form.productId} onChange={setField("productId")} required>
                        <option value=''>{t("funcionario.select.placeholder")}</option>
                        {products.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                      </select>
                      {selectedProduct && (
                        <span className='text-secondary-light text-sm'>
                          {t("consumo.hint.available", { qty: selectedProduct.availableQuantity || 0 })}
                        </span>
                      )}
                    </div>
                    <div className='col-sm-3'>
                      <label className='form-label'>{t("consumo.field.quantity")}</label>
                      <div className='icon-field'>
                        <span className='icon'>
                          <Icon icon='solar:box-minimalistic-outline' />
                        </span>
                        <input type='number' min='0' className='form-control' value={form.quantity} onChange={setField("quantity")} required />
                      </div>
                    </div>
                    <div className='col-sm-3'>
                      <label className='form-label'>{t("consumo.field.unitType")}</label>
                      <select className='form-select' value={form.unitTypeId} onChange={setField("unitTypeId")}>
                        <option value='package'>{t("consumo.unitType.package")}</option>
                        <option value='base'>{t("consumo.unitType.base")}</option>
                      </select>
                    </div>
                    <div className='col-sm-6'>
                      <label className='form-label'>{t("consumo.field.date")}</label>
                      <div className='icon-field'>
                        <span className='icon'>
                          <Icon icon='solar:calendar-outline' />
                        </span>
                        <input type='date' className='form-control' value={form.consumptionDate} onChange={setField("consumptionDate")} />
                      </div>
                    </div>
                  </div>
                  <div className='form-group d-flex align-items-center justify-content-end mt-24'>
                    <button type='button' className='form-wizard-next-btn btn btn-primary-600 px-32' onClick={nextStep}>
                      {t("global.next")} <i className='ri-arrow-right-line' />
                    </button>
                  </div>
                </fieldset>
              )}

              {!done && currentStep === 2 && (
                <fieldset className='wizard-fieldset show'>
                  <div className='alert alert-info'>{t("consumo.confirm.description")}</div>
                  <ul className='list-unstyled d-flex flex-column gap-8'>
                    <li>
                      <strong>{t("consumo.field.product")}:</strong> {productName(form.productId)}
                    </li>
                    <li>
                      <strong>{t("consumo.field.quantity")}:</strong> {form.quantity}
                    </li>
                    <li>
                      <strong>{t("consumo.field.unitType")}:</strong>{" "}
                      {form.unitTypeId === "package" ? t("consumo.unitType.package") : t("consumo.unitType.base")}
                    </li>
                    <li>
                      <strong>{t("consumo.field.date")}:</strong> {form.consumptionDate}
                    </li>
                  </ul>
                  <div className='form-group d-flex align-items-center justify-content-end gap-8 mt-24'>
                    <button
                      type='button'
                      className='form-wizard-previous-btn btn btn-neutral-500 border-neutral-100 px-32'
                      onClick={prevStep}
                      disabled={submitting}
                    >
                      {t("global.previous")}
                    </button>
                    <button type='button' className='form-wizard-submit btn btn-primary-600 px-32' onClick={handleSubmit} disabled={submitting}>
                      {submitting ? (
                        t("global.loading")
                      ) : (
                        <>
                          <i className='ri-check-line' /> {t("global.confirm")}
                        </>
                      )}
                    </button>
                  </div>
                </fieldset>
              )}

              {done && (
                <fieldset className='wizard-fieldset show'>
                  <div className='text-center mb-40'>
                    <span className='w-64-px h-64-px bg-success-focus text-success-main rounded-circle d-inline-flex justify-content-center align-items-center mb-16 text-2xl'>
                      <i className='ri-check-line' />
                    </span>
                    <h6 className='text-md text-neutral-600'>{t("consumo.success.created")}</h6>
                  </div>
                  <div className='form-group d-flex align-items-center justify-content-center gap-8'>
                    <button type='button' className='btn btn-neutral-500 border-neutral-100 px-32' onClick={restart}>
                      {t("consumo.action.createAnother")}
                    </button>
                    <button
                      type='button'
                      className='btn btn-primary-600 px-32'
                      onClick={() => navigate("/despesas/consumo")}
                    >
                      <i className='ri-arrow-left-line' /> {t("consumo.breadcrumb.list")}
                    </button>
                  </div>
                </fieldset>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CadastrarConsumoLayer;
